import type { HTMLAttributes } from 'react';

export interface ProgressBarProps extends Omit<HTMLAttributes<HTMLDivElement>, 'role'> {
  /** Completed count. */
  value: number;
  /** Total count. When 0, the bar renders empty (no divide-by-zero). */
  total: number;
  /** Accessible label (e.g. "Progress"). */
  label: string;
  /** Visual size. `sm` is used for per-epic bars, `md` (default) for the header. */
  size?: 'sm' | 'md';
}

/**
 * Determinate progress bar. Track + accent fill, both token-driven. Used by
 * the project header (done/total work items) and per-epic rows. Reaching
 * 100% switches the fill to the "done" green via `data-complete`.
 */
export function ProgressBar({
  value,
  total,
  label,
  size = 'md',
  className,
  ...rest
}: ProgressBarProps) {
  const pct = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
  const complete = total > 0 && value >= total ? '1' : '0';
  const cls = ['pn-progress', size === 'sm' ? 'pn-progress-sm' : '', className]
    .filter(Boolean)
    .join(' ');
  return (
    <div
      className={cls}
      role="progressbar"
      aria-label={label}
      aria-valuenow={total > 0 ? Math.min(value, total) : value}
      aria-valuemin={0}
      aria-valuemax={total}
      data-complete={complete}
      {...rest}
    >
      <span className="pn-progress-fill" style={{ width: `${pct}%` }} />
    </div>
  );
}
